import type { RetailOrderLineItem } from '~/api/graphql/generated/types'
import { discountEditRoleBlacklist } from '~/constants/roleNames'
import { useIsChewyCheckoutEnabled } from '~/store/hooks/business'
import { useHasGroupedParentWithFixedPrice } from '~/store/hooks/charges'
import type { InvoiceLineItem } from '~/types'
import { getIsRetailOrderLineItem } from '~/utils/invoiceUtils'
import useHasRole from '~/utils/useHasRole'

import { useGetIsDeclinedLineItem } from './useDeclineOrderItem'

/**
 * Hook to determine if the discount calculator button should be hidden
 * Matches the logic from ChargeFieldDiscountAmount and DiscountCellBody
 *
 * Hides calculator when ANY of these conditions are true:
 * 1. editDisabled prop is true
 * 2. User has role in discountEditRoleBlacklist (VCR, CWAV, ClientConcierge)
 * 3. Item is RetailOrderLineItem (Chewy.com items)
 * 4. Item has discountAllowed: false AND no existing discount (covers outside pharmacy)
 * 5. Item is declined
 * 6. Parent has fixed price
 */
export const useShouldHideDiscountCalculator = (
  item: InvoiceLineItem | RetailOrderLineItem,
  parentItem?: InvoiceLineItem | RetailOrderLineItem,
  editDisabled?: boolean,
): boolean => {
  const isChewyCheckoutEnabled = useIsChewyCheckoutEnabled()
  const isRetailOrderLineItem =
    getIsRetailOrderLineItem(item) && isChewyCheckoutEnabled

  const canEditDiscount = !useHasRole(discountEditRoleBlacklist)
  const hasParentWithFixedPrice = useHasGroupedParentWithFixedPrice(parentItem)
  const isDeclined = useGetIsDeclinedLineItem(item)

  // Check if item has discountAllowed: false AND no existing discount
  // This matches the hyphen condition from DiscountCellBody.tsx
  // Outside pharmacy items have discountAllowed: false from backend, so they're covered here
  const canHaveDiscount = getIsRetailOrderLineItem(item)
    ? false // RetailOrderLineItem has no concept of discountAllowed
    : item.discountAllowed

  const hasNoDiscountAllowed =
    !canHaveDiscount &&
    !(item as InvoiceLineItem).discountAmount &&
    !(item as InvoiceLineItem).discountPerc

  // Hide calculator when ANY condition is true
  return (
    Boolean(editDisabled) ||
    !canEditDiscount ||
    isRetailOrderLineItem ||
    hasNoDiscountAllowed ||
    isDeclined ||
    hasParentWithFixedPrice
  )
}
